// Account Performance table — FE-036 / spec §7.0.7.
//
// Sortable + paginated. State lives in the parent
// ``AccountPerformanceSection`` so the React-Query key changes
// cleanly per click. Mirrors the FE-033 EmployeePerformanceTable
// shape; columns + sort keys are tuned to the BE-029 backend
// allow-list (``completed_count`` / ``open_count`` /
// ``overdue_count`` / ``blocked_count`` / ``completion_rate`` /
// ``on_time_percent`` / ``legal_name``).

import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

import {
  buildAccountFilterUrl,
  buildCommitmentFilterUrl,
  type ClientPerformanceRow,
} from "@/features/dashboard";
import { Table, THead, TBody, Tr, Th, Td } from "@/design-system";

import { RiskBadge } from "./RiskBadge";


export type AccountPerformanceSortKey =
  | "completed_count" | "open_count" | "overdue_count"
  | "blocked_count" | "completion_rate" | "on_time_percent"
  | "legal_name";


export interface AccountPerformanceTableProps {
  rows: ClientPerformanceRow[];
  total: number;
  sortBy: AccountPerformanceSortKey;
  sortDir: "asc" | "desc";
  onSortChange: (next: {
    sortBy: AccountPerformanceSortKey;
    sortDir: "asc" | "desc";
  }) => void;
  limit: number;
  offset: number;
  onOffsetChange: (next: number) => void;
}


interface ColumnConfig {
  key:
    | "legal_name" | "completed_count" | "open_count"
    | "overdue_count" | "blocked_count" | "completion_rate"
    | "on_time_percent" | "renewal_date" | "risk_bucket";
  label: string;
  sortKey?: AccountPerformanceSortKey;
  align?: "left" | "right" | "center";
  testKey?: string;
}


const COLUMNS: ColumnConfig[] = [
  { key: "legal_name", label: "Account", sortKey: "legal_name",
    align: "left", testKey: "legal-name" },
  { key: "completed_count", label: "Completed",
    sortKey: "completed_count", align: "right", testKey: "completed" },
  { key: "open_count", label: "Open",
    sortKey: "open_count", align: "right", testKey: "open" },
  { key: "overdue_count", label: "Overdue",
    sortKey: "overdue_count", align: "right", testKey: "overdue" },
  { key: "blocked_count", label: "Blocked",
    sortKey: "blocked_count", align: "right", testKey: "blocked" },
  { key: "completion_rate", label: "Completion",
    sortKey: "completion_rate", align: "right", testKey: "completion-rate" },
  { key: "on_time_percent", label: "On-time",
    sortKey: "on_time_percent", align: "right", testKey: "on-time" },
  { key: "renewal_date", label: "Renewal",
    align: "left", testKey: "renewal" },
  { key: "risk_bucket", label: "Risk",
    align: "center", testKey: "risk" },
];


export function AccountPerformanceTable({
  rows, total, sortBy, sortDir, onSortChange,
  limit, offset, onOffsetChange,
}: AccountPerformanceTableProps) {
  const showingTo = Math.min(offset + rows.length, total);
  const showingFrom = rows.length === 0 ? 0 : offset + 1;
  const hasPrev = offset > 0;
  const hasNext = offset + rows.length < total;
  return (
    <div data-testid="account-performance-table" className="flex flex-col">
      <Table wrapperClassName="rounded-md border border-border-subtle">
        <THead className="bg-recessed text-xs font-medium uppercase tracking-wide text-text-tertiary">
            <Tr>
              {COLUMNS.map((col) => (
                <Th
                  key={col.key}
                  className={[
                    col.align === "right" ? "text-right" : "",
                    col.align === "center" ? "text-center" : "",
                  ].join(" ")}
                  aria-sort={
                    col.sortKey === sortBy
                      ? sortDir === "asc" ? "ascending" : "descending"
                      : col.sortKey ? "none" : undefined
                  }
                >
                  {col.sortKey ? (
                    <SortHeaderButton
                      label={col.label}
                      active={col.sortKey === sortBy}
                      sortDir={col.sortKey === sortBy ? sortDir : null}
                      onClick={() =>
                        onSortChange(
                          col.sortKey === sortBy
                            ? {
                                sortBy: col.sortKey,
                                sortDir: sortDir === "asc" ? "desc" : "asc",
                              }
                            : { sortBy: col.sortKey!, sortDir: "desc" },
                        )
                      }
                    />
                  ) : (
                    col.label
                  )}
                </Th>
              ))}
              <Th className="w-8 text-right">
                <span className="sr-only">Drill through</span>
              </Th>
            </Tr>
          </THead>
          <TBody>
            {rows.map((row) => (
              <AccountRow key={row.account_id} row={row} />
            ))}
          </TBody>
      </Table>

      <div className="mt-2 flex items-center justify-between text-xs text-text-tertiary">
        <span data-testid="account-performance-pagination-summary">
          {total === 0
            ? "0 accounts"
            : `Showing ${showingFrom}–${showingTo} of ${total}`}
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            data-testid="account-performance-prev"
            disabled={!hasPrev}
            onClick={() => onOffsetChange(Math.max(0, offset - limit))}
            aria-label="Previous page"
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-text-secondary disabled:cursor-not-allowed disabled:text-text-tertiary hover:bg-recessed"
          >
            <ChevronLeft size={14} aria-hidden="true" />
          </button>
          <button
            type="button"
            data-testid="account-performance-next"
            disabled={!hasNext}
            onClick={() => onOffsetChange(offset + limit)}
            aria-label="Next page"
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-text-secondary disabled:cursor-not-allowed disabled:text-text-tertiary hover:bg-recessed"
          >
            <ChevronRight size={14} aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
}


function AccountRow({ row }: { row: ClientPerformanceRow }) {
  return (
    <Tr
      data-testid={`account-performance-row-${row.account_id}`}
      className="border-t border-border-subtle"
    >
      <Td
        data-testid={`cell-legal-name-${row.account_id}`}
        className="text-text-primary"
      >
        <Link
          to={buildAccountFilterUrl({ id: row.account_id })}
          data-testid={`account-link-${row.account_id}`}
          className="hover:underline"
        >
          {row.legal_name}
        </Link>
      </Td>
      <Td
        data-testid={`cell-completed-${row.account_id}`}
        className="text-right text-text-primary"
      >
        {row.completed_count}
      </Td>
      <Td
        data-testid={`cell-open-${row.account_id}`}
        className="text-right text-text-primary"
      >
        {row.open_count}
      </Td>
      <Td
        data-testid={`cell-overdue-${row.account_id}`}
        className="text-right text-text-primary"
      >
        {row.overdue_count > 0 ? (
          <Link
            to={buildCommitmentFilterUrl({
              client_id: row.account_id, overdue: true,
            })}
            data-testid={`overdue-link-${row.account_id}`}
            className="font-medium text-ember hover:underline"
          >
            {row.overdue_count}
          </Link>
        ) : (
          row.overdue_count
        )}
      </Td>
      <Td
        data-testid={`cell-blocked-${row.account_id}`}
        className="text-right text-text-primary"
      >
        {row.blocked_count > 0 ? (
          <Link
            to={buildCommitmentFilterUrl({
              client_id: row.account_id, is_blocked: true,
            })}
            data-testid={`blocked-link-${row.account_id}`}
            className="font-medium text-blocked hover:underline"
          >
            {row.blocked_count}
          </Link>
        ) : (
          row.blocked_count
        )}
      </Td>
      <Td
        data-testid={`cell-completion-rate-${row.account_id}`}
        className="text-right text-text-primary"
      >
        {`${Math.round(row.completion_rate * 100)}%`}
      </Td>
      <Td
        data-testid={`cell-on-time-${row.account_id}`}
        className="text-right text-text-primary"
      >
        {`${Math.round(row.on_time_percent)}%`}
      </Td>
      <Td
        data-testid={`cell-renewal-${row.account_id}`}
        className="text-text-secondary"
      >
        {row.renewal_date ? formatRenewalDate(row.renewal_date) : "—"}
      </Td>
      <Td
        data-testid={`cell-risk-${row.account_id}`}
        className="text-center"
      >
        <RiskBadge bucket={row.risk_bucket} />
      </Td>
      <Td className="text-right">
        <Link
          to={buildAccountFilterUrl({ id: row.account_id })}
          data-testid={`drill-${row.account_id}`}
          aria-label={`Open ${row.legal_name} in the Accounts list`}
          className="inline-flex h-7 w-7 items-center justify-center rounded-full text-text-tertiary transition-fast ease-out hover:bg-recessed hover:text-text-primary"
        >
          <ChevronRight size={14} aria-hidden="true" />
        </Link>
      </Td>
    </Tr>
  );
}


function SortHeaderButton({
  label, active, sortDir, onClick,
}: {
  label: string;
  active: boolean;
  sortDir: "asc" | "desc" | null;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={
        active
          ? `Sort by ${label}, currently ${sortDir === "asc" ? "ascending" : "descending"}`
          : `Sort by ${label}`
      }
      className={[
        "inline-flex items-center gap-1 transition-fast ease-out",
        active ? "text-text-primary" : "hover:text-text-primary",
      ].join(" ")}
    >
      <span>{label}</span>
      {active && sortDir === "asc" ? (
        <ArrowUp size={10} aria-hidden="true" />
      ) : active && sortDir === "desc" ? (
        <ArrowDown size={10} aria-hidden="true" />
      ) : null}
    </button>
  );
}


function formatRenewalDate(iso: string): string {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    month: "short", day: "numeric", year: "numeric",
  });
}
